import api from './api';

/**
 * Catálogos fiscais para os campos NCM e CEST — o que antes era digitação livre
 * (com 5 NCMs hardcoded como sugestão) agora é busca de verdade.
 *
 * NCM  → BrasilAPI (`/api/ncm/v1?search=`), tabela oficial do Siscomex, busca
 *        por código ou por descrição. Consulta pública, com CORS liberado, então
 *        vai direto do browser — sem proxy no nosso backend.
 * CEST → endpoint próprio `/cests`, alimentado pela tabela do Convênio ICMS
 *        142/2018 carregada por migration. Não existe API pública gratuita de
 *        CEST; a tabela é nossa.
 *
 * Nenhuma das duas buscas é bloqueante para o cadastro: se a rede falhar, a
 * função devolve lista vazia e o campo continua aceitando digitação livre — um
 * catálogo fora do ar não pode impedir o usuário de cadastrar um produto.
 */

export interface FiscalCodeOption {
  codigo: string;
  descricao: string;
  /** Linha auxiliar exibida abaixo da descrição (segmento do CEST). */
  detalhe?: string;
}

const BRASIL_API_NCM = 'https://brasilapi.com.br/api/ncm/v1';

/** Cache por termo — o Autocomplete refaz a busca a cada tecla (com debounce). */
const cacheNcm = new Map<string, FiscalCodeOption[]>();

export function somenteDigitos(valor: string): string {
  return (valor || '').replace(/\D/g, '');
}

/**
 * Busca NCM por código ou descrição. A BrasilAPI devolve o código pontuado
 * ("8419.89.91"); guardamos e devolvemos só os 8 dígitos, que é o que a NFC-e
 * e o resto do sistema usam.
 */
export async function buscarNcm(termo: string): Promise<FiscalCodeOption[]> {
  const search = (termo || '').trim();
  if (search.length < 2) return [];

  const emCache = cacheNcm.get(search);
  if (emCache) return emCache;

  try {
    const response = await fetch(
      `${BRASIL_API_NCM}?search=${encodeURIComponent(search)}`,
    );
    if (!response.ok) return [];

    const data = await response.json();
    if (!Array.isArray(data)) return [];

    const options = data
      .map((item: { codigo?: string; descricao?: string }) => ({
        codigo: somenteDigitos(item.codigo || ''),
        descricao: item.descricao || '',
      }))
      // A tabela tem capítulos/posições (menos de 8 dígitos) que não são NCM
      // válido para produto — a NFC-e exige os 8.
      .filter((item: FiscalCodeOption) => item.codigo.length === 8);

    cacheNcm.set(search, options);
    return options;
  } catch {
    return [];
  }
}

/**
 * Busca CEST. Quando `ncm` é informado, devolve só os CESTs aplicáveis àquele
 * NCM — que é a regra do convênio e evita o erro clássico de colar um CEST de
 * outro segmento.
 */
export async function buscarCest(params: {
  ncm?: string;
  search?: string;
}): Promise<FiscalCodeOption[]> {
  try {
    const { data } = await api.get('/cests', {
      params: {
        ncm: params.ncm ? somenteDigitos(params.ncm) : undefined,
        search: params.search || undefined,
        limit: 50,
      },
    });

    if (!Array.isArray(data)) return [];

    return data.map(
      (item: {
        codigo: string;
        descricao: string;
        segmento_descricao?: string;
      }) => ({
        codigo: item.codigo,
        descricao: item.descricao,
        detalhe: item.segmento_descricao,
      }),
    );
  } catch {
    return [];
  }
}

/**
 * CST do IBS/CBS (IT 2025.002) — são 18 códigos, então buscamos a lista inteira
 * uma vez e filtramos no cliente; o servidor não precisa entrar no meio de cada
 * tecla digitada.
 */
let cacheCst: FiscalCodeOption[] | null = null;

export async function buscarCstIbsCbs(
  termo: string,
): Promise<FiscalCodeOption[]> {
  try {
    if (!cacheCst) {
      const { data } = await api.get('/classificacao-tributaria/csts');
      cacheCst = Array.isArray(data)
        ? data.map((item: { codigo: string; nome: string }) => ({
            codigo: item.codigo,
            descricao: item.nome,
          }))
        : [];
    }

    const busca = (termo || '').trim().toLowerCase();
    if (!busca) return cacheCst;

    return cacheCst.filter(
      (item) =>
        item.codigo.startsWith(somenteDigitos(busca)) ||
        item.descricao.toLowerCase().includes(busca),
    );
  } catch {
    return [];
  }
}

/**
 * cClassTrib do CST informado. `apenasNfce` (padrão) corta a tabela de 161 para
 * as 40 classificações que a NFC-e aceita — o resto é de documento que o PDV
 * não emite.
 */
export async function buscarCClassTrib(params: {
  cst?: string;
  search?: string;
  apenasNfce?: boolean;
}): Promise<FiscalCodeOption[]> {
  try {
    const { data } = await api.get('/classificacao-tributaria/cclass', {
      params: {
        cst: params.cst ? somenteDigitos(params.cst) : undefined,
        search: params.search || undefined,
        nfce: params.apenasNfce === false ? 'false' : undefined,
      },
    });

    if (!Array.isArray(data)) return [];

    return data.map(
      (item: {
        codigo: string;
        nome: string;
        nome_reduzido?: string | null;
        perc_red_ibs?: number | null;
        perc_red_cbs?: number | null;
      }) => {
        const reducao =
          item.perc_red_ibs || item.perc_red_cbs
            ? `Redução IBS ${item.perc_red_ibs ?? 0}% · CBS ${
                item.perc_red_cbs ?? 0
              }%`
            : undefined;

        return {
          codigo: item.codigo,
          descricao: item.nome_reduzido || item.nome,
          detalhe: reducao,
        };
      },
    );
  } catch {
    return [];
  }
}

export default {
  buscarNcm,
  buscarCest,
  buscarCstIbsCbs,
  buscarCClassTrib,
  somenteDigitos,
};
